import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "../theme";
import {
  MAX_SCHEDULE_ROWS,
  parseImageList,
  parseScheduleRows,
} from "@shared/signage-rows";
import { MAX_SLIDESHOW_IMAGES } from "@shared/types/signage-template";
import AssetsPicker from "./AssetsPicker";
import TemplateSlide from "../../tv/signage/TemplateSlide";
import type {
  ResolvedSignageTemplate,
  SignageTemplate,
  SignageTemplateField,
  SignageTemplateInstance,
} from "@shared/types/signage-template";

/**
 * Fill a template in - the LOWER tier of the two this feature is built around.
 *
 * The whole form is generated from the template's declared `fields`. There is
 * no per-template code here and there must never be: a designer adding a field
 * gets a control for it, and that is the entire contract between the two roles.
 *
 * ## What this surface deliberately does NOT offer
 *
 * Position, colour, font, size, and the designer's baked-in regions. A filler
 * supplies DATA. That is the slot doctrine from `docs/template-system.md`
 * applied exactly where it still holds - the signage template system inverts it
 * one level up, for the designer, and preserves it here.
 *
 * ## The preview is the real renderer
 *
 * `TemplateSlide` is the same component the wall runs, driven by the values
 * being typed. Not an approximation of it: a preview that draws the slide its
 * own way is a preview that can disagree with the wall, which is the one thing
 * it exists to rule out.
 */

export interface SignageSlideDialogProps {
  open: boolean;
  onClose: () => void;
  /** Absent = building a new slide. */
  slide?: SignageTemplateInstance | null;
  /** Every template this scope can see - its own, its chain's, Nenda's. */
  templates: SignageTemplate[];
  onSave: (body: {
    name: string;
    templateId: string;
    values: Record<string, string>;
  }) => Promise<void>;
  saving?: boolean;
  error?: string | null;
}

/** What the live preview needs, built from the design plus what is typed. */
export function previewSlide(
  template: SignageTemplate,
  values: Record<string, string>
): ResolvedSignageTemplate {
  return {
    instanceId: "preview",
    templateId: template.id,
    version: template.version,
    canvas: template.canvas,
    ...(template.background ? { background: template.background } : {}),
    regions: template.regions,
    values,
  };
}

/**
 * A picked library picture, applied to a field's value: an image field is
 * REPLACED, an image rotation is APPENDED to (one URL per line, no repeats,
 * capped at what the renderer will cycle through).
 */
export function withPicked(
  type: SignageTemplateField["type"] | undefined,
  current: string,
  url: string
): string {
  if (type !== "images") return url;
  const list = parseImageList(current, MAX_SLIDESHOW_IMAGES);
  if (list.includes(url) || list.length >= MAX_SLIDESHOW_IMAGES) {
    return list.join("\n");
  }
  return [...list, url].join("\n");
}

/** Which fields are required and still empty. */
export function missingRequired(
  template: SignageTemplate | undefined,
  values: Record<string, string>
): SignageTemplateField[] {
  if (!template) return [];
  return template.fields.filter(
    (f) => f.required && !(values[f.key] ?? "").trim()
  );
}

export default function SignageSlideDialog({
  open,
  onClose,
  slide,
  templates,
  onSave,
  saving,
  error,
}: SignageSlideDialogProps) {
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [picking, setPicking] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(slide?.name ?? "");
    setTemplateId(slide?.templateId ?? templates[0]?.id ?? "");
    setValues(slide?.values ?? {});
    setPicking(null);
  }, [open, slide, templates]);

  const template = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId]
  );

  /*
   * Changing the template KEEPS values whose key still exists.
   *
   * Two templates that both ask for `headline` are the common case - a chain's
   * summer and winter designs - and clearing the form because somebody picked
   * the other one would make switching feel like starting over. Keys the new
   * design does not ask for are dropped, because the store would drop them too.
   */
  const switchTemplate = (id: string) => {
    const next = templates.find((t) => t.id === id);
    setTemplateId(id);
    setValues((prev) => {
      if (!next) return prev;
      const keep: Record<string, string> = {};
      for (const field of next.fields) {
        const carried = prev[field.key];
        if (carried) keep[field.key] = carried;
      }
      return keep;
    });
  };

  const missing = missingRequired(template, values);
  const canSave =
    name.trim().length > 0 &&
    Boolean(template) &&
    missing.length === 0 &&
    !saving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>
        {slide ? "Edit slide" : "New slide"}
      </DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: "column", md: "row" }} gap={3} sx={{ pt: 0.5 }}>
          {/* The form. Everything here is declared by the designer. */}
          <Stack gap={2} sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              size="small"
              label="Name"
              placeholder="October offer"
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ "data-testid": "slide-name" }}
              helperText="What you will look for in the list. Never shown on the screen."
            />

            <TextField
              select
              size="small"
              label="Template"
              value={templateId}
              onChange={(e) => switchTemplate(e.target.value)}
              disabled={templates.length === 0}
              helperText={
                templates.length === 0
                  ? "No templates yet. Somebody with template design rights makes one first."
                  : undefined
              }
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                  {t.scope !== "premise" && (
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontSize: 12, color: tokens.inkMuted }}
                    >
                      · {t.scope === "global" ? "Nenda" : "Company"}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </TextField>

            {template?.fields.length === 0 && (
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                This template has nothing to fill in — it is the same on every
                screen that shows it.
              </Typography>
            )}

            {template?.fields.map((field) => (
              <FieldControl
                key={field.key}
                field={field}
                value={values[field.key] ?? ""}
                onChange={(v) =>
                  setValues((prev) => ({ ...prev, [field.key]: v }))
                }
                onPick={() => setPicking(field.key)}
              />
            ))}
          </Stack>

          {/* The preview: the SAME component the wall runs. */}
          <Box sx={{ width: { xs: "100%", md: 420 }, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 0.5 }}>
              How it will look
            </Typography>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: template
                  ? `${template.canvas.width} / ${template.canvas.height}`
                  : "16 / 9",
                border: `1px solid ${tokens.divider}`,
                borderRadius: "8px",
                overflow: "hidden",
                bgcolor: "#000",
              }}
            >
              {template ? (
                <SlidePreview template={template} values={values} />
              ) : null}
            </Box>
          </Box>
        </Stack>

        {error && (
          <Typography sx={{ mt: 2, fontSize: 13, color: tokens.error }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() => void onSave({ name: name.trim(), templateId, values })}
        >
          {slide ? "Save slide" : "Create slide"}
        </Button>
      </DialogActions>

      <AssetsPicker
        open={picking !== null}
        onClose={() => setPicking(null)}
        onSelect={(url) => {
          if (picking) {
            const f = template?.fields.find((x) => x.key === picking);
            setValues((prev) => ({
              ...prev,
              [picking]: withPicked(f?.type, prev[picking] ?? "", url),
            }));
          }
          setPicking(null);
        }}
      />
    </Dialog>
  );
}

/**
 * The preview, scaled into whatever box the dialog gives it.
 *
 * `TemplateSlide` positions itself `fixed` because on a wall it IS the screen.
 * Here it is contained instead: the wrapper is the positioning context and the
 * inner surface is pinned to it, which is the same trick `LivePreview` uses to
 * put a 1920px TV in a 460px column.
 */
function SlidePreview({
  template,
  values,
}: {
  template: SignageTemplate;
  values: Record<string, string>;
}) {
  return (
    <Box
      data-testid="slide-preview"
      // The renderer positions itself `absolute; inset: 0` and scales to
      // whatever box it is given, so it needs nothing but a box.
      sx={{ position: "absolute", inset: 0 }}
    >
      <TemplateSlide
        slide={previewSlide(template, values)}
        timezone="Europe/Stockholm"
      />
    </Box>
  );
}

/**
 * The pictures of an image rotation: thumbnails in order, each removable, and
 * one button to add another from the library. Shared by the fill form, the
 * care unit's update page and the designer (for a fixed rotation).
 */
export function ImageListControl({
  label,
  value,
  onChange,
  onPick,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onPick: () => void;
  help?: string;
}) {
  const images = parseImageList(value, MAX_SLIDESHOW_IMAGES);
  const full = images.length >= MAX_SLIDESHOW_IMAGES;
  return (
    <Stack gap={0.75}>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {images.map((src, i) => (
          <Box
            key={`${i}-${src}`}
            sx={{
              position: "relative",
              width: 72,
              height: 48,
              borderRadius: "6px",
              overflow: "hidden",
              border: `1px solid ${tokens.divider}`,
            }}
          >
            <Box
              component="img"
              src={src}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <Box
              component="button"
              type="button"
              aria-label={`Remove picture ${i + 1}`}
              onClick={() =>
                onChange(images.filter((_, j) => j !== i).join("\n"))
              }
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                width: 18,
                height: 18,
                lineHeight: "16px",
                p: 0,
                border: 0,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.65)",
                color: "#fff",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              ×
            </Box>
          </Box>
        ))}
      </Stack>
      <Stack direction="row" gap={1} alignItems="center">
        <Button size="small" onClick={onPick} disabled={full}>
          Add picture
        </Button>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {images.length === 0
            ? "No pictures yet."
            : `${images.length} of up to ${MAX_SLIDESHOW_IMAGES}`}
        </Typography>
      </Stack>
      {help && (
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {help}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * One control, chosen by the field's declared type.
 *
 * A picture is PICKED from the media library, never typed: an operator pasting
 * a path they guessed is the single most common way a slide ends up drawing
 * nothing, and the library is the only place a valid one comes from.
 */
export function FieldControl({
  field,
  value,
  onChange,
  onPick,
}: {
  field: SignageTemplateField;
  value: string;
  onChange: (v: string) => void;
  onPick: () => void;
}) {
  const required = Boolean(field.required);
  const empty = !value.trim();

  if (field.type === "images") {
    return (
      <ImageListControl
        label={field.label + (required ? " *" : "")}
        value={value}
        onChange={onChange}
        onPick={onPick}
        help={field.help}
      />
    );
  }

  if (field.type === "image") {
    return (
      <Stack gap={0.5}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {field.label}
          {required && " *"}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 72,
              height: 48,
              flexShrink: 0,
              borderRadius: "6px",
              border: `1px solid ${tokens.divider}`,
              bgcolor: tokens.rowAlt,
              overflow: "hidden",
            }}
          >
            {value && (
              <Box
                component="img"
                src={value}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
          </Box>
          <Button size="small" onClick={onPick}>
            {value ? "Change picture" : "Choose picture"}
          </Button>
          {value && (
            <Button size="small" color="inherit" onClick={() => onChange("")}>
              Clear
            </Button>
          )}
        </Stack>
        {field.help && (
          <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
            {field.help}
          </Typography>
        )}
        {required && empty && (
          <Typography sx={{ fontSize: 12, color: tokens.error }}>
            Needed before this slide can be saved.
          </Typography>
        )}
      </Stack>
    );
  }

  /*
   * A `rows` field is a plain textarea, on purpose.
   *
   * Somebody putting today's classes on a wall has them in an email or a
   * spreadsheet: they paste, fix a line, save. A row-by-row form with add and
   * remove buttons is slower for every case and much slower for twelve of them
   * - and it would be the "too many weird input fields" mistake again.
   */
  if (field.type === "rows") {
    const rows = parseScheduleRows(value);
    return (
      <Stack gap={0.5}>
        <TextField
          size="small"
          label={field.label + (required ? " *" : "")}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          multiline
          rows={8}
          placeholder={
            "07:00 | Morning yoga\n09:30 | Spinning\nSauna open all day"
          }
          error={required && empty}
          inputProps={{ "data-testid": `field-${field.key}` }}
          helperText={
            field.help ??
            "One per line. Put a time first with a | after it, or just write the line."
          }
        />
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {rows.length === 0
            ? "Nothing yet."
            : `${rows.length} ${rows.length === 1 ? "line" : "lines"}`}
          {rows.length >= MAX_SCHEDULE_ROWS &&
            ` · only the first ${MAX_SCHEDULE_ROWS} will be shown`}
        </Typography>
      </Stack>
    );
  }

  const multiline = field.type === "multiline";
  return (
    <TextField
      size="small"
      label={field.label + (required ? " *" : "")}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      multiline={multiline}
      rows={multiline ? 3 : undefined}
      placeholder={field.type === "url" ? "https://…" : undefined}
      error={required && empty}
      inputProps={{
        "data-testid": `field-${field.key}`,
        ...(field.maxLength ? { maxLength: field.maxLength } : {}),
      }}
      helperText={
        required && empty
          ? "Needed before this slide can be saved."
          : // The ceiling is the DESIGN's, not a validation rule: past it the
            // words stop fitting the space the designer drew for them.
            [
              field.help,
              field.maxLength ? `Up to ${field.maxLength} characters` : "",
            ]
              .filter(Boolean)
              .join(" · ") || undefined
      }
    />
  );
}
